import { FormActionFrameworkStep, registerStep } from '../step';
import { resolveTemplate } from '../template';
export class PopulateDropdownStep extends FormActionFrameworkStep {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 'populateDropdown'
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: {
                fieldName: '',
                values: '',
            }
        });
    }
    async execute(context) {
        const resolvedField = resolveTemplate(resolveFieldName(this.config) || this.config.fieldName || this.config.elementId || '', context);
        const fieldName = resolveFieldName({
            fieldName: String(resolvedField ?? ''),
        });
        if (!fieldName) {
            context.log('[populateDropdown] missing fieldName — skipped');
            return context;
        }
        const resolvedValues = resolveTemplate(this.config.values, context);
        const choices = resolveChoices(resolvedValues);
        const model = context.form;
        // Same locator as smart fill: SurveyJS question name / data-name.
        const question = findSurveyQuestion(model, fieldName);
        if (!question) {
            // Last-resort DOM fallback for native <select> hosts (not SurveyJS).
            if (typeof document !== 'undefined') {
                const el = document.querySelector(`[data-name="${cssEscape(fieldName)}"]`) ||
                    document.querySelector(`#${cssEscape(fieldName)}`);
                if (el instanceof HTMLSelectElement) {
                    el.innerHTML = '';
                    for (const choice of choices) {
                        const opt = document.createElement('option');
                        opt.value = String(choice.value);
                        opt.textContent = choice.text;
                        el.appendChild(opt);
                    }
                    context.log(`[populateDropdown] populated native select ${fieldName} (${choices.length} choices)`);
                    return context;
                }
            }
            context.log(`[populateDropdown] question not found for fieldName="${fieldName}" — skipped`);
            return context;
        }
        question.choices = choices;
        if (choices.length === 0) {
            context.log(`[populateDropdown] cleared choices on ${fieldName}`);
        }
        else {
            context.log(`[populateDropdown] set choices on ${fieldName} (${choices.length} items)`);
        }
        return context;
    }
    showStepForm(container, _context, onUpdated) {
        const cfg = this.config;
        const fieldName = resolveFieldName(cfg);
        const valuesText = typeof cfg.values === 'string'
            ? cfg.values
            : cfg.values != null
                ? JSON.stringify(cfg.values, null, 2)
                : '';
        container.innerHTML = `
      <div class="mb-3">
        <label class="form-label">Step name</label>
        <input type="text" class="form-control" data-faf="name" value="${escapeAttr(this.name)}" />
      </div>
      <div class="mb-3">
        <label class="form-label">Field name (SurveyJS name / data-name)</label>
        <input type="text" class="form-control" data-faf="fieldName" value="${escapeAttr(fieldName)}" placeholder="surveyjs_data_name" />
        <div class="text-muted small">Same as smart fill — not DOM id, data-key, or aria-label. Supports {{ }}.</div>
      </div>
      <div class="mb-3">
        <label class="form-label">values</label>
        <textarea class="form-control" data-faf="values" rows="5">${escapeText(valuesText)}</textarea>
        <div class="text-muted small">CSV, JSON array, or {{variable}}. Supports {{ }} templates.</div>
      </div>
    `;
        const sync = () => {
            this.name = inputValue(container, 'name');
            this.config.fieldName = inputValue(container, 'fieldName');
            delete this.config.elementId;
            delete this.config.incidentId;
            delete this.config.taskId;
            delete this.config.formId;
            this.config.values = parseValuesField(textareaValue(container, 'values'));
            onUpdated?.();
        };
        container.querySelectorAll('input, textarea').forEach((el) => {
            el.addEventListener('change', sync);
            el.addEventListener('blur', sync);
        });
    }
}
/**
 * Prefer fieldName; fall back to legacy elementId.
 * If a dotted path is pasted (e.g. panel.field[0]), keep the leaf name —
 * matching smart fill's scrollToSurveyField resolution.
 */
export function resolveFieldName(config) {
    const raw = (typeof config.fieldName === 'string' && config.fieldName.trim()) ||
        (typeof config.elementId === 'string' && config.elementId.trim()) ||
        '';
    if (!raw)
        return '';
    return raw.split('.').pop()?.replace(/\[\d+\]$/, '') || raw;
}
/**
 * Locate a SurveyJS question the same way smart fill does:
 * getQuestionByName, then a light page.elements scan as fallback.
 */
export function findSurveyQuestion(model, fieldName) {
    if (!model || !fieldName)
        return null;
    const byName = model.getQuestionByName?.(fieldName);
    if (byName)
        return byName;
    for (const page of model.pages || []) {
        const match = (page.elements || []).find((el) => el.name === fieldName);
        if (match)
            return match;
    }
    return null;
}
export function resolveChoices(values) {
    if (values == null)
        return [];
    if (typeof values === 'string') {
        const trimmed = values.trim();
        if (!trimmed)
            return [];
        if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
            try {
                return resolveChoices(JSON.parse(trimmed));
            }
            catch {
                // fall through to CSV
            }
        }
        return trimmed
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean)
            .map((part) => ({ value: part, text: part }));
    }
    if (Array.isArray(values)) {
        return values.map((item) => {
            if (item != null && typeof item === 'object' && !Array.isArray(item)) {
                const record = item;
                const value = (record.value ?? record.id ?? record.name ?? '');
                const text = String(record.text ?? record.label ?? record.name ?? value);
                return { value, text };
            }
            return { value: item, text: String(item) };
        });
    }
    if (typeof values === 'object') {
        return Object.entries(values).map(([key, val]) => ({
            value: key,
            text: String(val),
        }));
    }
    return [];
}
function parseValuesField(raw) {
    const trimmed = raw.trim();
    if (!trimmed)
        return '';
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
        try {
            return JSON.parse(trimmed);
        }
        catch {
            return trimmed;
        }
    }
    return trimmed;
}
function inputValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value?.trim() ?? '';
}
function textareaValue(container, key) {
    const el = container.querySelector(`[data-faf="${key}"]`);
    return el?.value ?? '';
}
function cssEscape(value) {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
function escapeAttr(value) {
    if (value == null)
        return '';
    return String(value)
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;');
}
function escapeText(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;');
}
registerStep('populateDropdown', PopulateDropdownStep);
